"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { useForm } from "react-hook-form";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { toast } from "sonner";

import { ApiError } from "@/lib/api/api-error";
import { submitContactMutation } from "@/components/pages/contact/api/contact";
import { contactInquiryQueryToFormValue } from "@/lib/contact/contact-inquiry-params";
import {
  ContactInquiryType,
  ContactLocationScale,
  createContactFormSchema,
  type ContactFormFieldValues,
} from "@/lib/contact/contact-form-schema";
import {
  FormField,
  SelectInput,
  TextArea,
  TextInput,
} from "@/components/ui/form-field";
import { cn } from "@/lib/utils";

type ContactFormProps = {
  inquiryQueryType?: string;
};

function defaultInquiryFromQuery(inquiryQueryType?: string): ContactInquiryType {
  if (!inquiryQueryType) return ContactInquiryType.Executive;
  return (contactInquiryQueryToFormValue[inquiryQueryType] ??
    ContactInquiryType.Executive) as ContactInquiryType;
}

export function ContactForm({ inquiryQueryType }: ContactFormProps) {
  const locale = useLocale() as "en" | "vi";
  const t = useTranslations("catalog");

  const urlMapped = inquiryQueryType
    ? contactInquiryQueryToFormValue[inquiryQueryType]
    : undefined;
  const [inquiryHighlight, setInquiryHighlight] = useState(() =>
    Boolean(urlMapped),
  );

  useEffect(() => {
    if (!urlMapped) return;
    const id = window.setTimeout(() => setInquiryHighlight(false), 1500);
    return () => window.clearTimeout(id);
  }, [urlMapped]);

  const schema = useMemo(
    () =>
      createContactFormSchema({
        required: t("contact.form.validation.required"),
        email: t("contact.form.validation.email"),
        phone: t("contact.form.validation.phone"),
        messageMax: t("contact.form.validation.messageMax"),
        nameMax: t("contact.form.validation.nameMax"),
        companyMax: t("contact.form.validation.companyMax"),
        businessAddressMax: t("contact.form.validation.businessAddressMax"),
      }),
    [t],
  );

  const defaultInquiry = defaultInquiryFromQuery(inquiryQueryType);

  const defaultValues = useMemo<ContactFormFieldValues>(
    () => ({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      company: "",
      businessAddress: "",
      locationScale: ContactLocationScale.Small,
      inquiryType: defaultInquiry,
      message: "",
    }),
    [defaultInquiry],
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting: formSubmitting },
    reset,
    setError,
  } = useForm<ContactFormFieldValues>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: "onTouched",
  });

  const privacyHref = `/${locale}/privacy`;

  const submitMutation = useMutation(submitContactMutation());

  const onSubmit = handleSubmit(async (data) => {
    try {
      await submitMutation.mutateAsync({ ...data, locale });
      toast.success(t("contact.form.thankYou"));
      reset({
        ...defaultValues,
        inquiryType: defaultInquiryFromQuery(inquiryQueryType),
      });
    } catch (e) {
      if (ApiError.is(e) && e.status === 422) {
        const fe = e.fieldErrors;
        let applied = false;
        if (fe) {
          for (const key of Object.keys(
            fe,
          ) as (keyof ContactFormFieldValues)[]) {
            const msg = fe[key]?.[0];
            if (msg) {
              setError(key, { type: "server", message: msg });
              applied = true;
            }
          }
        }
        if (applied) return;
      }
      toast.error(t("contact.form.submitError"));
    }
  });

  return (
    <form className="space-y-6" onSubmit={onSubmit} noValidate>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <FormField
          id="contact-first-name"
          label={t("contact.form.firstName")}
          error={errors.firstName?.message}
        >
          {(p) => (
            <TextInput
              {...p}
              type="text"
              autoComplete="given-name"
              hasError={Boolean(errors.firstName)}
              {...register("firstName")}
            />
          )}
        </FormField>
        <FormField
          id="contact-last-name"
          label={t("contact.form.lastName")}
          error={errors.lastName?.message}
        >
          {(p) => (
            <TextInput
              {...p}
              type="text"
              autoComplete="family-name"
              hasError={Boolean(errors.lastName)}
              {...register("lastName")}
            />
          )}
        </FormField>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <FormField
          id="contact-email"
          label={t("contact.form.email")}
          error={errors.email?.message}
        >
          {(p) => (
            <TextInput
              {...p}
              type="email"
              autoComplete="email"
              placeholder={t("contact.form.emailPlaceholder")}
              hasError={Boolean(errors.email)}
              {...register("email")}
            />
          )}
        </FormField>
        <FormField
          id="contact-phone"
          label={t("contact.form.phone")}
          error={errors.phone?.message}
        >
          {(p) => (
            <TextInput
              {...p}
              type="tel"
              autoComplete="tel"
              hasError={Boolean(errors.phone)}
              {...register("phone")}
            />
          )}
        </FormField>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <FormField
          id="contact-company"
          label={t("contact.form.company")}
          error={errors.company?.message}
        >
          {(p) => (
            <TextInput
              {...p}
              type="text"
              autoComplete="organization"
              hasError={Boolean(errors.company)}
              {...register("company")}
            />
          )}
        </FormField>
        <FormField
          id="contact-locations"
          label={t("contact.form.locations")}
          error={errors.locationScale?.message}
        >
          {(p) => (
            <SelectInput
              {...p}
              hasError={Boolean(errors.locationScale)}
              {...register("locationScale", { valueAsNumber: true })}
            >
              <option value={ContactLocationScale.Small}>{t("contact.form.loc1")}</option>
              <option value={ContactLocationScale.Medium}>{t("contact.form.loc2")}</option>
              <option value={ContactLocationScale.Large}>{t("contact.form.loc3")}</option>
              <option value={ContactLocationScale.Enterprise}>{t("contact.form.loc4")}</option>
            </SelectInput>
          )}
        </FormField>
      </div>

      <FormField
        id="contact-business-address"
        label={t("contact.form.businessAddress")}
        error={errors.businessAddress?.message}
      >
        {(p) => (
          <TextInput
            {...p}
            type="text"
            autoComplete="street-address"
            hasError={Boolean(errors.businessAddress)}
            {...register("businessAddress")}
          />
        )}
      </FormField>

      <FormField
        id="contact-inquiry-type"
        label={t("contact.form.inquiry")}
        error={errors.inquiryType?.message}
      >
        {(p) => (
          <SelectInput
            {...p}
            className={cn(
              "border-charcoal-400 font-semibold",
              inquiryHighlight &&
                "border-brand-red shadow-[0_0_0_1px_rgba(255,0,0,0.35)]",
            )}
            hasError={Boolean(errors.inquiryType)}
            {...register("inquiryType", { valueAsNumber: true })}
          >
            <option value={ContactInquiryType.Executive}>
              {t("contact.form.inqExecutive")}
            </option>
            <option value={ContactInquiryType.Architecture}>
              {t("contact.form.inqArchitecture")}
            </option>
            <option value={ContactInquiryType.Solution}>
              {t("contact.form.inqSolution")}
            </option>
            <option value={ContactInquiryType.Partnership}>
              {t("contact.form.inqPartnership")}
            </option>
            <option value={ContactInquiryType.Developer}>
              {t("contact.form.inqDeveloper")}
            </option>
            <option value={ContactInquiryType.General}>
              {t("contact.form.inqGeneral")}
            </option>
          </SelectInput>
        )}
      </FormField>

      <FormField
        id="contact-message"
        label={t("contact.form.message")}
        error={errors.message?.message}
      >
        {(p) => (
          <TextArea
            {...p}
            rows={4}
            placeholder={t("contact.form.messagePlaceholder")}
            hasError={Boolean(errors.message)}
            {...register("message")}
          />
        )}
      </FormField>

      <div className="pt-4">
        <button
          type="submit"
          disabled={formSubmitting || submitMutation.isPending}
          className="w-full rounded-sm bg-charcoal-900 py-4 text-sm font-bold uppercase tracking-widest text-white transition-colors duration-300 hover:bg-brand-red active:bg-brand-darkRed disabled:cursor-not-allowed disabled:opacity-60"
        >
          {formSubmitting || submitMutation.isPending
            ? t("contact.form.submitting")
            : t("contact.form.submit")}
        </button>
      </div>

      <p className="mt-4 text-center text-fs-sm text-charcoal-500">
        {t.rich("contact.form.disclaimer", {
          privacy: (chunks) => (
            <Link
              href={privacyHref}
              className="underline hover:text-charcoal-900"
            >
              {chunks}
            </Link>
          ),
        })}
      </p>
    </form>
  );
}
